/* eslint-disable */
import { useState, useEffect, useMemo, lazy, Suspense } from 'react'
import {
  Container,
  Box,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material'
import HomeIcon from '@mui/icons-material/Home'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined'
import { propertyAPI, masterDataAPI, propertyDraftAPI } from '../../services/api'
import { PropertyCard } from '../../Components/PropertyCard'
import { ViewModeSelector } from '../../Components/ViewModeSelector'
import { useAuth } from '../../Contexts/AuthContext'
import { useToast } from '../../context/ToastContext'
import { formatPrice } from '../../Utils/formatters'
import { transformProperties } from '../../Utils/propertyTransform'
import { encryptRouteParam } from '../../Utils/encrypt-decrypt'
import MainContentWrapper from '../../Layouts/MainContent/mainContent'
import { ADMIN_AVAILABILITY_OPTIONS } from '../../Constants/adminAvailabilityOptions'
import './myProperties.scss'

const PropertyMapView = lazy(() => import('../../Components/PropertyMapView'))
const PropertyListingForm = lazy(() => import('../../Components/PropertyListingForm'))

const extractCoordinatesFromMapLink = (mapLink) => {
  if (!mapLink || typeof mapLink !== 'string') return null

  try {
    const pattern1 = /@(-?\d+\.?\d*),(-?\d+\.?\d*),/
    const match1 = mapLink.match(pattern1)
    if (match1) {
      return {
        lat: parseFloat(match1[1]),
        lng: parseFloat(match1[2]),
      }
    }

    const pattern2 = /!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/
    const match2 = mapLink.match(pattern2)
    if (match2) {
      return {
        lat: parseFloat(match2[1]),
        lng: parseFloat(match2[2]),
      }
    }

    const url = new URL(mapLink)
    const latParam = url.searchParams.get('lat') || url.searchParams.get('latitude')
    const lngParam = url.searchParams.get('lng') || url.searchParams.get('longitude')
    if (latParam && lngParam) {
      return {
        lat: parseFloat(latParam),
        lng: parseFloat(lngParam),
      }
    }

    return null
  } catch (error) {
    return null
  }
}

const parseCoordinatesFromString = (value) => {
  if (!value || typeof value !== 'string') return null

  const input = value.trim()
  if (!input) return null

  const decimalMatch = input.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/)
  if (decimalMatch) {
    return {
      lat: parseFloat(decimalMatch[1]),
      lng: parseFloat(decimalMatch[2]),
    }
  }

  const degreeMatch = input.match(/(\d+\.?\d*)°?\s*[NS],?\s*(\d+\.?\d*)°?\s*[EW]/i)
  if (degreeMatch) {
    return {
      lat: parseFloat(degreeMatch[1]),
      lng: parseFloat(degreeMatch[2]),
    }
  }

  return extractCoordinatesFromMapLink(input)
}

const resolvePropertyCoordinates = (property) => {
  if (!property || typeof property !== 'object') return null

  if (property.geoLocation && typeof property.geoLocation === 'object') {
    const mapLink = property.geoLocation.mapLink || property.geoLocation.map_link
    const fromGeoMap = extractCoordinatesFromMapLink(mapLink)
    if (fromGeoMap) return fromGeoMap

    const lat = parseFloat(property.geoLocation.latitude ?? property.geoLocation.lat)
    const lng = parseFloat(property.geoLocation.longitude ?? property.geoLocation.lng)
    if (!isNaN(lat) && !isNaN(lng)) return { lat, lng }
  }

  if (property.location && typeof property.location === 'object') {
    const mapLink = property.location.mapLink || property.location.map_link
    const fromMapLink = extractCoordinatesFromMapLink(mapLink)
    if (fromMapLink) return fromMapLink

    const lat = parseFloat(property.location.latitude ?? property.location.lat)
    const lng = parseFloat(property.location.longitude ?? property.location.lng)
    if (!isNaN(lat) && !isNaN(lng)) return { lat, lng }
  }

  if (typeof property.location === 'string') {
    const fromLocationString = parseCoordinatesFromString(property.location)
    if (fromLocationString) return fromLocationString
  }

  const mapFields = [
    property.googleMapLocation,
    property.google_map_location,
    property.mapLink,
    property.map_link,
  ]
  for (const field of mapFields) {
    const coords = parseCoordinatesFromString(field)
    if (coords) return coords
  }

  const lat = parseFloat(property.latitude ?? property.lat)
  const lng = parseFloat(property.longitude ?? property.lng)
  if (!isNaN(lat) && !isNaN(lng)) return { lat, lng }

  return null
}

const isValidMapCoordinate = (coords) => {
  if (!coords) return false
  return (
    Number.isFinite(coords.lat) &&
    Number.isFinite(coords.lng) &&
    coords.lat >= 10 && coords.lat <= 20 &&
    coords.lng >= 72 && coords.lng <= 80
  )
}

const getDeterministicFallbackCoordinates = (property, index) => {
  const seedText = String(
    property?.property_id || property?.id || property?.property_name || `property-${index}`
  )

  let hash = 0
  for (let i = 0; i < seedText.length; i += 1) {
    hash = ((hash << 5) - hash + seedText.charCodeAt(i)) | 0
  }

  const positiveHash = Math.abs(hash)
  const lat = 11 + ((positiveHash % 8000) / 1000)
  const lng = 74 + ((Math.floor(positiveHash / 8000) % 5500) / 1000)

  return {
    lat: Number(lat.toFixed(6)),
    lng: Number(lng.toFixed(6)),
  }
}

const getValueFromPath = (obj, path) => {
  if (!obj || typeof obj !== 'object') return undefined
  return path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj)
}

const normalizePrefillValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === 'string' ? item.trim() : String(item || '').trim()))
      .filter(Boolean)
      .join(', ')
  }

  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  return ''
}

const getPrefillValue = (property, keys, fallback = '') => {
  for (const key of keys) {
    const value = getValueFromPath(property, key)
    const normalized = normalizePrefillValue(value)
    if (normalized) {
      return normalized
    }
  }
  return fallback
}

const normalizeSearchKeywordsForDisplay = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item.trim()
        if (item && typeof item === 'object') {
          return String(
            item.keyword ||
            item.searchKeyword ||
            item.search_keyword ||
            item.value ||
            item.name ||
            '',
          ).trim()
        }
        return String(item || '').trim()
      })
      .filter(Boolean)
      .join(', ')
  }

  if (value && typeof value === 'object') {
    return String(value.keyword || value.searchKeyword || value.search_keyword || value.value || '').trim()
  }

  if (typeof value === 'string') {
    return value
      .split(/[;,|]/)
      .map((item) => item.trim())
      .filter(Boolean)
      .join(', ')
  }

  if (typeof value === 'number') return String(value)
  return ''
}

const getSearchKeywordsPrefillValue = (property, keys, fallback = '') => {
  for (const key of keys) {
    const value = getValueFromPath(property, key)
    const normalized = normalizeSearchKeywordsForDisplay(value)
    if (normalized) {
      return normalized
    }
  }
  return fallback
}

const getFirstValue = (...values) => {
  for (const value of values) {
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      return value
    }
  }
  return ''
}

const formatTableDate = (value) => {
  if (!value) return 'Not set'
  const dateValue = new Date(value)
  if (Number.isNaN(dateValue.getTime())) return 'Not set'
  return dateValue.toISOString().slice(0, 10)
}

const formatTablePrice = (value) => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric) || numeric <= 0) return 'N/A'
  return formatPrice(numeric)
}

export function PropertiesUnderVerification({ embeddedInAdminPortal = false }) {
  const theme = useTheme()
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'))
  const { user, isLoggedIn } = useAuth()
  const toast = useToast()

  const [properties, setProperties] = useState([])
  const [mapProperties, setMapProperties] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [statusOptions, setStatusOptions] = useState([])
  const [individualStatusCounts, setIndividualStatusCounts] = useState([])
  const [statusPageStart, setStatusPageStart] = useState(0)
  const [viewMode, setViewMode] = useState('list')
  const [propertyTypes, setPropertyTypes] = useState([])
  const [isListingFormOpen, setIsListingFormOpen] = useState(false)
  const [shouldRefreshOnListingClose, setShouldRefreshOnListingClose] = useState(false)
  const [editingProperty, setEditingProperty] = useState(null)
  const [availabilityStatuses, setAvailabilityStatuses] = useState([])
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)
  const [updatingProperty, setUpdatingProperty] = useState(null)
  const [updateFormData, setUpdateFormData] = useState({
    currentAvailability: 'Pending Verification',
    leadBy: '',
    flashcardKeyword: '',
    searchKeywords: '',
    estimatedCurrentValue: '',
  })
  const [isUpdatingVerification, setIsUpdatingVerification] = useState(false)
  const [isLoadingMapData, setIsLoadingMapData] = useState(false)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    hasMore: false,
  })
  const statusPageSize = useMemo(() => {
    if (isMobileView) return 1
    return 2
  }, [isMobileView])

  useEffect(() => {
    setStatusPageStart(0)
  }, [statusPageSize])

  useEffect(() => {
    fetchUnderVerificationProperties(1, false, statusFilter, typeFilter, debouncedSearchTerm)
  }, [statusFilter, typeFilter, debouncedSearchTerm])

  const handleSearchKeyDown = (event) => {
    if (event.key !== 'Enter') return
    setDebouncedSearchTerm(searchTerm.trim())
  }

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const response = await masterDataAPI.getAllMasterData()
        const data = response.data.data
        setPropertyTypes(data.propertyTypes || [])
        setAvailabilityStatuses(data.availabilityStatuses || [])
      } catch (error) {
        console.error('Error fetching master data:', error)
      }
    }

    fetchMasterData()
  }, [])

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await masterDataAPI.fetchStatus()
        const payload = response?.data?.data
        const statuses = Array.isArray(payload)
          ? payload
          : (payload?.statuses || payload?.statusList || response?.data?.statuses || response?.data?.data || [])

        if (Array.isArray(statuses)) {
          const normalizedStatuses = statuses
            .map((item, index) => {
              if (typeof item === 'string' || typeof item === 'number') {
                return {
                  id: index + 1,
                  value: String(item),
                  label: String(item),
                }
              }

              if (item && typeof item === 'object') {
                let rawValue = item.status ?? item.value ?? item.name ?? item.label

                if (rawValue && typeof rawValue === 'object') {
                  rawValue =
                    rawValue.status ??
                    rawValue.value ??
                    rawValue.name ??
                    rawValue.label ??
                    Object.values(rawValue).find((value) => typeof value === 'string' || typeof value === 'number')
                }

                if (typeof rawValue === 'string' || typeof rawValue === 'number') {
                  const primitive = String(rawValue)
                  return {
                    id: item.id ?? index + 1,
                    value: primitive,
                    label: primitive,
                  }
                }
              }

              return null
            })
            .filter(Boolean)

          setStatusOptions(normalizedStatuses)
        }
      } catch (error) {
        console.error('Error fetching statuses:', error)
      }
    }

    fetchStatuses()
  }, [])

  const fetchUnderVerificationProperties = async (
    page = 1,
    append = false,
    selectedStatus = statusFilter,
    selectedType = typeFilter,
    selectedSearch = debouncedSearchTerm,
  ) => {
    try {
      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }

      const filters = {
        page,
        limit: 12,
        sortBy: 'newest',
        status: selectedStatus || 'all',
      }

      if (selectedType) {
        filters.propertyType = selectedType
      }

      if (selectedSearch) {
        filters.q = selectedSearch
      }

      const response = await propertyAPI.getAllProperties(filters)
      const fetchedProperties = response?.data?.data?.properties || response?.data?.properties || response?.data?.data || []
      const transformedProperties = transformProperties(fetchedProperties)

      if (append) {
        setProperties((prev) => [...prev, ...transformedProperties])
      } else {
        setProperties(transformedProperties)
        setMapProperties([])
      }

      setIndividualStatusCounts(Array.isArray(response?.data?.individualStatusCounts) ? response.data.individualStatusCounts : [])
      setStatusPageStart(0)

      setPagination({
        page: response?.data?.page || page,
        limit: response?.data?.limit || 12,
        total: response?.data?.total || (append ? properties.length + transformedProperties.length : transformedProperties.length),
        hasMore: Boolean(response?.data?.hasMore),
      })
    } catch (err) {
      toast.showError(err.response?.data?.message || 'Failed to load properties')
    } finally {
      if (append) {
        setLoadingMore(false)
      } else {
        setLoading(false)
      }
    }
  }

  const handleViewDetails = async (property) => {
    const isDraft = property.isDraft === true || property.status?.toLowerCase() === 'draft' || property.status?.toLowerCase() === 'pending'
    const draftId = property.draft_id || property.draftId

    if (isDraft && draftId) {
      window.open(`/property/draft/${draftId}`, '_blank', 'noopener,noreferrer')
      return
    }

    const actualPropertyId = property.property_id || property.propertyId || property.propertyCode || property.id
    const encryptedPropertyId = encryptRouteParam(actualPropertyId)
    window.open(`/property/${encryptedPropertyId}`, '_blank', 'noopener,noreferrer')
  }

  const handleEditProperty = (property) => {
    setEditingProperty(property)
    setShouldRefreshOnListingClose(true)
    setIsListingFormOpen(true)
  }

  const handleDeleteProperty = async (property) => {
    const isDraft = property.isDraft === true || property.status?.toLowerCase() === 'draft' || property.status?.toLowerCase() === 'pending'
    const propertyId = property.property_id || property.id
    const draftId = property.draft_id || property.draftId

    if (window.confirm('Are you sure you want to delete this property?')) {
      try {
        if (isDraft && draftId) {
          await propertyDraftAPI.deleteDraftById(draftId)
        } else {
          await propertyAPI.deleteProperty(propertyId)
        }
        toast.showSuccess('Property deleted successfully')
        sessionStorage.removeItem('propertiesCache')
        fetchUnderVerificationProperties(1, false, statusFilter, typeFilter, debouncedSearchTerm)
      } catch (err) {
        toast.showError(err.response?.data?.message || 'Failed to delete property')
      }
    }
  }

  const handleCloseListingForm = () => {
    setIsListingFormOpen(false)
    setEditingProperty(null)

    if (shouldRefreshOnListingClose) {
      sessionStorage.removeItem('propertiesCache')
      fetchUnderVerificationProperties(1, false, statusFilter, typeFilter, debouncedSearchTerm)
    }

    setShouldRefreshOnListingClose(false)
  }

  const handleOpenListingForm = () => {
    setEditingProperty(null)
    setShouldRefreshOnListingClose(true)
    setIsListingFormOpen(true)
  }

  const handlePropertySaved = () => {
    setShouldRefreshOnListingClose(false)
    sessionStorage.removeItem('propertiesCache')
    fetchUnderVerificationProperties(1, false, statusFilter, typeFilter, debouncedSearchTerm)
  }

  const openUpdateVerificationModal = (property) => {
    setUpdatingProperty(property)
    setUpdateFormData({
      currentAvailability: getPrefillValue(
        property,
        ['currentAvailability', 'current_availability', 'status', 'availability', 'propertyStatus'],
        'Pending Verification',
      ),
      leadBy: getPrefillValue(property, ['leadBy', 'lead_by', 'lead', 'leadName']),
      flashcardKeyword: getPrefillValue(
        property,
        ['flashcardKeyword', 'flashcard_keyword', 'flashcardKeywords', 'flashcard_keywords', 'seo.flashcardKeyword'],
      ),
      searchKeywords: getSearchKeywordsPrefillValue(
        property,
        ['searchKeywords', 'search_keywords', 'searchKeyword', 'search_keyword', 'seo.searchKeywords'],
      ),
      estimatedCurrentValue: getPrefillValue(
        property,
        ['estimatedCurrentValue', 'estimated_current_value', 'estimatedValue'],
      ),
    })
    setIsUpdateModalOpen(true)
  }

  const closeUpdateVerificationModal = () => {
    setIsUpdateModalOpen(false)
    setUpdatingProperty(null)
  }

  const handleUpdateVerification = async () => {
    const propertyId =
      updatingProperty?.property_id ||
      updatingProperty?.propertyId ||
      updatingProperty?.property_code ||
      updatingProperty?.propertyCode ||
      updatingProperty?.id

    if (!propertyId) {
      toast.showError('Unable to identify property for update')
      return
    }

    try {
      setIsUpdatingVerification(true)
      let existingPropertyData = updatingProperty || {}

      const hasRequiredType = Boolean(existingPropertyData?.property_type || existingPropertyData?.propertyType)
      if (!hasRequiredType) {
        const propertyDetailResponse = await propertyAPI.getPropertyById(propertyId)
        existingPropertyData =
          propertyDetailResponse?.data?.data?.property ||
          propertyDetailResponse?.data?.property ||
          propertyDetailResponse?.data?.data ||
          propertyDetailResponse?.data ||
          {}
      }

      const {
        lead_by,
        flashcard_keyword,
        search_keywords,
        estimated_current_value,
        ...existingPropertyPayload
      } = existingPropertyData

      const mergedUpdatePayload = {
        ...existingPropertyPayload,
        property_id: propertyId,
        propertyId: propertyId,
        currentAvailability: updateFormData.currentAvailability,
        current_availability: updateFormData.currentAvailability,
        leadBy: updateFormData.leadBy,
        flashcardKeyword: updateFormData.flashcardKeyword,
        searchKeywords: updateFormData.searchKeywords,
        estimatedCurrentValue: updateFormData.estimatedCurrentValue,
      }

      if (!mergedUpdatePayload.property_type && !mergedUpdatePayload.propertyType) {
        toast.showError('Property type is missing. Please refresh and try again.')
        return
      }

      await propertyAPI.updateProperty(propertyId, mergedUpdatePayload)
      toast.showSuccess('Verification details updated successfully')
      closeUpdateVerificationModal()
      fetchUnderVerificationProperties(1, false, statusFilter)
    } catch (err) {
      toast.showError(err.response?.data?.message || 'Failed to update verification details')
    } finally {
      setIsUpdatingVerification(false)
    }
  }

  const handleLoadMore = async () => {
    if (loadingMore || !pagination.hasMore) return

    const nextPage = pagination.page + 1
    fetchUnderVerificationProperties(nextPage, true, statusFilter, typeFilter, debouncedSearchTerm)
  }

  const resolveStatusFilterValue = (statusLabel) => {
    const normalizedLabel = String(statusLabel || '').trim().toLowerCase()
    if (!normalizedLabel) return ''

    const matchingOption = statusOptions.find((option) => {
      const optionValue = String(option?.value || '').trim().toLowerCase()
      const optionLabel = String(option?.label || '').trim().toLowerCase()
      return optionValue === normalizedLabel || optionLabel === normalizedLabel
    })

    return matchingOption?.value || String(statusLabel).trim()
  }

  const handleStatusSummaryClick = (statusLabel) => {
    const nextStatusValue = resolveStatusFilterValue(statusLabel)
    setStatusFilter((prevStatus) => {
      const normalizedPrev = String(prevStatus || '').trim().toLowerCase()
      const normalizedNext = String(nextStatusValue || '').trim().toLowerCase()

      // Clicking the same status again clears the filter.
      if (normalizedPrev && normalizedPrev === normalizedNext) {
        return ''
      }

      return nextStatusValue
    })
  }

  const isStatusSummaryActive = (statusLabel) => {
    const selectedStatus = String(statusFilter || '').trim().toLowerCase()
    if (!selectedStatus) return false

    const resolvedStatus = String(resolveStatusFilterValue(statusLabel) || '').trim().toLowerCase()
    return resolvedStatus === selectedStatus
  }

  const fetchAllPropertiesForMapView = async () => {
    if (isLoadingMapData) return

    try {
      setIsLoadingMapData(true)

      const response = await propertyAPI.getAllProperties({
        page: 1,
        limit: 1000,
        sortBy: 'newest',
        status: statusFilter || 'all',
        ...(typeFilter ? { propertyType: typeFilter } : {}),
        ...(debouncedSearchTerm ? { q: debouncedSearchTerm } : {}),
      })

      const fetchedProperties = response?.data?.data?.properties || response?.data?.properties || response?.data?.data || []
      const transformedProperties = transformProperties(fetchedProperties)
      setMapProperties(transformedProperties)
    } catch (error) {
      toast.showError(error?.response?.data?.message || 'Failed to load all properties for map view')
    } finally {
      setIsLoadingMapData(false)
    }
  }

  useEffect(() => {
    if (viewMode !== 'map') return
    if (mapProperties.length > 0) return

    fetchAllPropertiesForMapView()
  }, [viewMode, statusFilter, typeFilter, debouncedSearchTerm, mapProperties.length])

  const getStatusClassName = (status) => {
    const normalized = String(status || '').trim().toLowerCase()

    if (normalized === 'available' || normalized === 'listed') return 'status-available'
    if (normalized === 'pending verification') return 'status-under-verification'
    if (normalized === 'inactive' || normalized === 'unlisted/inactive' || normalized === 'unlisted') return 'status-inactive'
    if (normalized === 'rejected') return 'rejected'
    if (normalized === 'unavailable') return 'status-inactive'
    if (normalized === 'archived') return 'status-default'
    if (normalized === 'sold by others') return 'status-sold-others'
    if (normalized === 'incomplete information') return 'status-incomplete-information'
    if (normalized === 'not responding') return 'status-not-responding'
    if (normalized === 'sold by vruddhi') return 'status-sold-vruddhi'
    if (normalized === 'additional options') return 'status-additional-options'
    if (normalized === 'draft') return 'status-draft'

    return 'status-default'
  }

  const draftStatusItem = useMemo(() => {
    return individualStatusCounts.find((item) => String(item?.status || '').trim().toLowerCase() === 'draft') || { status: 'Draft', count: 0 }
  }, [individualStatusCounts])

  const statusCountsForDisplay = useMemo(() => {
    const nonDraftStatuses = individualStatusCounts.filter(
      (item) => String(item?.status || '').trim().toLowerCase() !== 'draft',
    )

    return [...nonDraftStatuses, draftStatusItem]
  }, [individualStatusCounts, draftStatusItem])

  const totalCountFromStatusCards = useMemo(() => {
    return statusCountsForDisplay.reduce((sum, item) => sum + Number(item?.count || 0), 0)
  }, [statusCountsForDisplay])

  const visibleStatusCounts = useMemo(() => {
    if (!isMobileView) return statusCountsForDisplay
    return statusCountsForDisplay.slice(statusPageStart, statusPageStart + statusPageSize)
  }, [statusPageStart, statusCountsForDisplay, statusPageSize, isMobileView])

  const hasStatusPagination = isMobileView && statusCountsForDisplay.length > statusPageSize

  const handleNextStatusPage = () => {
    if (!hasStatusPagination) return
    const nextStart = statusPageStart + statusPageSize
    setStatusPageStart(nextStart >= statusCountsForDisplay.length ? 0 : nextStart)
  }

  const handlePreviousStatusPage = () => {
    if (!hasStatusPagination) return
    const prevStart = statusPageStart - statusPageSize
    if (prevStart < 0) {
      const remainder = statusCountsForDisplay.length % statusPageSize
      const lastPageStart = remainder === 0
        ? Math.max(statusCountsForDisplay.length - statusPageSize, 0)
        : Math.max(statusCountsForDisplay.length - remainder, 0)
      setStatusPageStart(lastPageStart)
      return
    }
    setStatusPageStart(prevStart)
  }

  // Search and filter are backend-driven to match Home page behavior.
  const displayedProperties = useMemo(() => properties, [properties])

  const mapDisplayedProperties = useMemo(() => mapProperties, [mapProperties])

  const mapSafeProperties = useMemo(() => {
    return mapDisplayedProperties.map((property, index) => {
      const resolvedCoordinates = resolvePropertyCoordinates(property)
      if (isValidMapCoordinate(resolvedCoordinates)) {
        return property
      }

      const fallbackCoordinates = getDeterministicFallbackCoordinates(property, index)

      return {
        ...property,
        latitude: String(fallbackCoordinates.lat),
        longitude: String(fallbackCoordinates.lng),
        lat: String(fallbackCoordinates.lat),
        lng: String(fallbackCoordinates.lng),
        googleMapLocation: `${fallbackCoordinates.lat}, ${fallbackCoordinates.lng}`,
        google_map_location: `${fallbackCoordinates.lat}, ${fallbackCoordinates.lng}`,
        geoLocation: {
          latitude: String(fallbackCoordinates.lat),
          longitude: String(fallbackCoordinates.lng),
          mapLink: '',
        },
        location: {
          latitude: String(fallbackCoordinates.lat),
          longitude: String(fallbackCoordinates.lng),
          mapLink: '',
        },
      }
    })
  }, [mapDisplayedProperties])

  const visibleProperties = viewMode === 'map' ? mapDisplayedProperties : displayedProperties

  if (loading) {
    if (embeddedInAdminPortal) {
      return (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )
    }

    return (
      <MainContentWrapper hideHeaderOnDesktop>
        <Container sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
      </MainContentWrapper>
    )
  }

  const content = (
    <Box className={embeddedInAdminPortal ? 'admin-manage-listings-page' : ''}>
      <Container
        maxWidth={false}
        disableGutters
        className={embeddedInAdminPortal ? 'admin-manage-listings-content' : ''}
        sx={{
          py: embeddedInAdminPortal ? 0 : 2,
          px: embeddedInAdminPortal ? 0 : { xs: 2, sm: 2, md: '40px', lg: '80px' },
          overflowX: 'hidden',
        }}
      >
        <div className='admin-manage-listings-toolbar'>
          <header className='page-header admin-manage-listings-header'>
            <div className='header-content'>
              {!embeddedInAdminPortal && <p className='page-breadcrumb'>Admin &gt; Manage Listings</p>}
              <h1 className='page-title'>Manage Listings</h1>
              <p className='page-subtitle page-subtitle-count'>
                {displayedProperties.length} of {pagination.total || properties.length} listings shown
              </p>
            </div>
            {embeddedInAdminPortal && (
              <div className='header-actions admin-manage-listings-top-actions'>
                <Button
                  variant='contained'
                  className='add-property admin-manage-listings-add-btn'
                  onClick={handleOpenListingForm}
                >
                  Add Listing
                </Button>
                <ViewModeSelector currentView={viewMode} onViewChange={setViewMode} showMap={true} />
              </div>
            )}
          </header>

          <section className={`summary-row summary-status-row ${isMobileView ? 'summary-status-row-mobile' : 'summary-status-row-desktop'}`}>
            <div
              className={`summary-card total ${!statusFilter ? 'summary-card-active' : ''}`}
              role='button'
              tabIndex={0}
              style={{ cursor: 'pointer' }}
              onClick={() => setStatusFilter('')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  setStatusFilter('')
                }
              }}
              aria-label='Show all status listings'
            >
              <div className='summary-icon'><HomeIcon /></div>
              <div className='summary-info'>
                <div className='summary-label'>Total</div>
                <div className='summary-count'>
                  {statusCountsForDisplay.length > 0 ? totalCountFromStatusCards : (pagination.total || properties.length)}
                </div>
              </div>
            </div>
            {hasStatusPagination && !isMobileView && (
              <button
                className='summary-inline-arrow'
                type='button'
                aria-label='Show previous status counts'
                onClick={handlePreviousStatusPage}
              >
                <ChevronLeftIcon fontSize='small' />
              </button>
            )}
            {visibleStatusCounts.length > 0
              ? (isMobileView ? visibleStatusCounts.slice(0, 1) : visibleStatusCounts).map((item) => (
                  <div
                    className={`summary-card ${getStatusClassName(item.status)} ${isStatusSummaryActive(item.status) ? 'summary-card-active' : ''}`}
                    key={String(item.status)}
                    role='button'
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleStatusSummaryClick(item.status)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        handleStatusSummaryClick(item.status)
                      }
                    }}
                    aria-label={`Filter listings by ${item.status}`}
                  >
                    <div className='summary-icon'><HomeIcon /></div>
                    <div className='summary-info'>
                      <div className='summary-label'>{item.status}</div>
                      <div className='summary-count'>{item.count || 0}</div>
                    </div>
                  </div>
                ))
              : (
                  <div
                    className={`summary-card status-draft ${isStatusSummaryActive(draftStatusItem.status) ? 'summary-card-active' : ''}`}
                    role='button'
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleStatusSummaryClick(draftStatusItem.status)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        handleStatusSummaryClick(draftStatusItem.status)
                      }
                    }}
                    aria-label={`Filter listings by ${draftStatusItem.status}`}
                  >
                    <div className='summary-icon'><HomeIcon /></div>
                    <div className='summary-info'>
                      <div className='summary-label'>{draftStatusItem.status}</div>
                      <div className='summary-count'>{draftStatusItem.count || 0}</div>
                    </div>
                  </div>
                )}
            {hasStatusPagination && (
              <button
                className='summary-inline-arrow'
                type='button'
                aria-label='Show next status counts'
                onClick={handleNextStatusPage}
              >
                <ChevronRightIcon fontSize='small' />
              </button>
            )}
          </section>

          <section className='controls-row'>
            <div className='search-wrap'>
              <input
                className='search-input'
                placeholder='Search properties by name, ID, location or contact'
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
            </div>
            <div className='filters-and-toggles'>
              <div className='filter-dropdowns'>
                <select
                  className='filter-dropdown'
                  value={typeFilter}
                  onChange={(event) => setTypeFilter(event.target.value)}
                >
                  <option value=''>All Types</option>
                  {propertyTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <select
                  className='filter-dropdown'
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option value=''>All Status</option>
                  {statusOptions.map((status) => {
                    const value = status?.value || ''
                    const label = status?.label || value
                    const optionKey = status?.id ?? value
                    return (
                      <option key={String(optionKey)} value={value}>
                        {label}
                      </option>
                    )
                  })}
                </select>
              </div>
              {!embeddedInAdminPortal && (
                <ViewModeSelector currentView={viewMode} onViewChange={setViewMode} showMap={true} />
              )}
            </div>
          </section>

          <div className='results-info'>
            Showing {visibleProperties.length} of {viewMode === 'map' ? visibleProperties.length : (pagination.total || properties.length)} properties
          </div>
        </div>

        <div className='admin-manage-listings-results'>
        {displayedProperties.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <h6 style={{ color: '#666', marginBottom: '16px' }}>
              No properties
            </h6>
          </Box>
        ) : viewMode === 'list' ? (
          <div className='admin-manage-list-table-wrap'>
            <table className='admin-manage-list-table'>
              <thead>
                <tr>
                  <th>PROPERTY</th>
                  <th>LOCATION</th>
                  <th>PRICE</th>
                  <th>STATUS</th>
                  <th>AVAILABILITY</th>
                  <th>LAST CHECKED</th>
                  <th className='actions-col'>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {displayedProperties.map((property) => {
                  const displayPropertyType = getFirstValue(property.propertyType, property.property_type)
                  const displayPropertySubType = getFirstValue(property.propertySubType, property.property_sub_type)
                  const propertyTypeSubTypeLabel = displayPropertyType && displayPropertySubType
                    ? `${displayPropertyType} - ${displayPropertySubType}`
                    : (displayPropertyType || displayPropertySubType || '-')
                  const flashcardKeyword = getFirstValue(
                    property.flashcardKeyword,
                    property.flashcard_keyword
                  )
                  const displayArea = getFirstValue(
                    property.area,
                    property.area_name,
                    property.areaName,
                    property.locality
                  )
                  const displayTaluk = getFirstValue(
                    property.taluk,
                    property.taluka,
                    property.taluk_name,
                    property.talukName
                  )
                  const locationLabel = getFirstValue(
                    [displayArea, displayTaluk].filter(Boolean).join(', '),
                    [property.address_line1, property.address_line2, property.city].filter(Boolean).join(', '),
                    'Not set'
                  )
                  const cardPrice = getFirstValue(
                    property.estimatedCurrentValue,
                    property.estimated_current_value,
                    property.expectedPrice,
                    property.expected_price,
                    0
                  )
                  const statusValue = getFirstValue(property.status, 'Unknown')
                  const availabilityValue = getFirstValue(
                    property.currentAvailability,
                    property.current_availability,
                    'Not set'
                  )
                  const statusClassName = getStatusClassName(statusValue)
                  const availabilityClassName = getStatusClassName(availabilityValue)
                  const checkedDate = formatTableDate(
                    getFirstValue(property.updatedAt, property.updated_at, property.createdAt, property.created_at)
                  )

                  return (
                    <tr key={property.property_id || property.id}>
                      <td>
                        <div
                          className='admin-table-property-title'
                          onClick={() => handleViewDetails(property)}
                          role='button'
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') handleViewDetails(property)
                          }}
                        >
                          {propertyTypeSubTypeLabel}
                        </div>
                        {flashcardKeyword && (
                          <div className='admin-table-property-keyword'>{flashcardKeyword}</div>
                        )}
                      </td>
                      <td>
                        <div className='admin-table-location'>
                          <LocationOnIcon className='admin-table-inline-icon' />
                          <span>{locationLabel}</span>
                        </div>
                      </td>
                      <td>
                        <span className='admin-table-price'>{formatTablePrice(cardPrice)}</span>
                      </td>
                      <td>
                        <span className={`admin-table-status-chip ${statusClassName}`}>{statusValue}</span>
                      </td>
                      <td>
                        <span className={`admin-table-status-chip ${availabilityClassName}`}>{availabilityValue}</span>
                      </td>
                      <td>
                        <span className='admin-table-date'>{checkedDate}</span>
                      </td>
                      <td>
                        <div className='admin-table-actions'>
                          <button
                            type='button'
                            className='admin-table-icon-btn'
                            aria-label='Edit property'
                            onClick={() => handleEditProperty(property)}
                          >
                            <EditOutlinedIcon />
                          </button>
                          <button
                            type='button'
                            className='admin-table-icon-btn'
                            aria-label='View property'
                            onClick={() => handleViewDetails(property)}
                          >
                            <VisibilityOutlinedIcon />
                          </button>
                          <button
                            type='button'
                            className='admin-table-icon-btn'
                            aria-label='Update admin fields'
                            onClick={() => openUpdateVerificationModal(property)}
                          >
                            <RuleOutlinedIcon />
                          </button>
                          <button
                            type='button'
                            className='admin-table-icon-btn danger'
                            aria-label='Delete property'
                            onClick={() => handleDeleteProperty(property)}
                          >
                            <DeleteOutlineIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : viewMode === 'map' ? (
          <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}>
            {isLoadingMapData && mapDisplayedProperties.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <PropertyMapView
                properties={mapSafeProperties}
                onViewDetails={handleViewDetails}
                isLoggedIn={isLoggedIn}
                user={user}
                renderPropertyCardPopup={true}
                showEditDelete={true}
                onEdit={handleEditProperty}
                onDelete={(prop) => handleDeleteProperty(prop)}
                disableContactButton={true}
                useUpdateAsSecondaryAction={true}
                onUpdateVerification={openUpdateVerificationModal}
              />
            )}
          </Suspense>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
                xl: 'repeat(4, 1fr)',
                xxl: 'repeat(4, 1fr)'
              },
              gap: 3,
              maxWidth: '100%',
              overflow: 'hidden',
              '& > *': {
                minHeight: 0,
                minWidth: 0
              }
            }}
          >
            {displayedProperties.map((property) => (
              <PropertyCard
                key={property.property_id || property.id}
                property={property}
                onViewDetails={handleViewDetails}
                isLoggedIn={isLoggedIn}
                user={user}
                showEditDelete={true}
                onEdit={handleEditProperty}
                onDelete={(prop) => handleDeleteProperty(prop)}
                disableContactButton={true}
                useUpdateAsSecondaryAction={true}
                onUpdateVerification={openUpdateVerificationModal}
              />
            ))}
          </Box>
        )}

        {pagination.hasMore && viewMode !== 'map' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
            <Button
              variant='contained'
              size='large'
              onClick={handleLoadMore}
              disabled={loadingMore}
              sx={{
                px: 6,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
              }}
            >
              {loadingMore ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                  Loading...
                </>
              ) : (
                'View More Properties'
              )}
            </Button>
          </Box>
        )}
        </div>

        <Dialog
          open={isUpdateModalOpen}
          onClose={closeUpdateVerificationModal}
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle>Admin Fields</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Availability</InputLabel>
                <Select
                  value={updateFormData.currentAvailability}
                  label='Availability'
                  onChange={(event) =>
                    setUpdateFormData((prev) => ({
                      ...prev,
                      currentAvailability: event.target.value,
                    }))
                  }
                >
                  {ADMIN_AVAILABILITY_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label='Flashcard Keyword'
                fullWidth
                value={updateFormData.flashcardKeyword}
                onChange={(event) =>
                  setUpdateFormData((prev) => ({
                    ...prev,
                    flashcardKeyword: event.target.value,
                  }))
                }
              />

              <TextField
                label='Search Keywords'
                fullWidth
                value={updateFormData.searchKeywords}
                onChange={(event) =>
                  setUpdateFormData((prev) => ({
                    ...prev,
                    searchKeywords: event.target.value,
                  }))
                }
              />

              <TextField
                label='Lead By'
                fullWidth
                value={updateFormData.leadBy}
                onChange={(event) =>
                  setUpdateFormData((prev) => ({
                    ...prev,
                    leadBy: event.target.value,
                  }))
                }
              />

              <TextField
                label='Estimated current value'
                fullWidth
                value={updateFormData.estimatedCurrentValue}
                onChange={(event) =>
                  setUpdateFormData((prev) => ({
                    ...prev,
                    estimatedCurrentValue: event.target.value,
                  }))
                }
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeUpdateVerificationModal} disabled={isUpdatingVerification}>
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={handleUpdateVerification}
              disabled={isUpdatingVerification}
            >
              {isUpdatingVerification ? 'Updating...' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        <Suspense fallback={null}>
          <PropertyListingForm
            isOpen={isListingFormOpen}
            onClose={handleCloseListingForm}
            editProperty={editingProperty}
            onSuccess={handlePropertySaved}
            saveMode='property-progress'
            forceAdminFields={embeddedInAdminPortal}
          />
        </Suspense>
      </Container>
    </Box>
  )

  if (embeddedInAdminPortal) {
    return content
  }

  return (
    <MainContentWrapper hideHeaderOnDesktop>
      {content}
    </MainContentWrapper>
  )
}
